import { buildSectionSelector, normalizeSectionId } from '@theme/section-renderer';

/**
 * `<lazy-content-section>` — loads block markup via the Section Rendering API when the element
 * nears the viewport, or uses HTML already present (theme editor / `?section_id=` response).
 *
 * **Storefront**
 * - Initial page response contains a placeholder only (no `{% content_for 'blocks' %}` in the document).
 * - Fetches `GET <current url>?section_id=<section.id>` and injects `.lazy-content-section__inner` children.
 *
 * **HTML contract**
 * - `data-section-id` (required for fetch): theme section id, same as `section.id` in Liquid.
 * - Placeholder: `[data-lazy-placeholder]` until content loads.
 * - Loaded body: `.lazy-content-section__inner` wrapping block output (from server on AJAX or editor).
 *
 * **Attributes (dataset)**
 * - `data-root-margin` — `IntersectionObserver` `rootMargin` (default `200px 0px`).
 * - `data-load-immediately` — `"true"` means blocks are already in the DOM (skip fetch).
 * - `data-loaded` — set when blocks are present (server) or after a successful client fetch.
 *
 * **Events**
 * - `lazy-content-section:loaded` — bubbles after content is in the document.
 * - `lazy-content-section:error` — bubbles on fetch/parse failure; `event.detail` has `{ error }`.
 *
 * @customElement lazy-content-section
 * @see https://shopify.dev/docs/api/ajax/section-rendering
 */

/**
 * IW nested-container extension:
 * - `data-block-id` identifies one lazy CX Container inside a larger CX Schedule response.
 * - All lazy containers from the same section share one network request and one parsed response.
 * - Returned scripts are recreated in document order so app blocks and theme components initialize.
 */
const sectionHtmlPromises = new Map();
const sectionDocumentPromises = new Map();
const sectionRetried = new Set();
let liveTvCardFormsRequested = false;

/**
 * A cold Section Rendering API response can stop inside the product loop and
 * leave the slider shell (lead image, empty track). The next request is complete.
 * @param {Document | Element} root
 * @returns {boolean}
 */
function documentHasEmptySlider(root) {
  const sliders = root.querySelectorAll('[data-cx-card-slider="true"]');
  for (const slider of sliders) {
    const track = slider.querySelector('.custom-container__cards');
    if (!track) continue;
    if (!track.querySelector('.custom-container__card')) return true;
  }
  return false;
}

class LazyContentSection extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {boolean} */
  #loadStarted = false;

  /** Wires the observer or skip path for editor/SSR content. */
  connectedCallback() {
    if (this.dataset.loadImmediately === 'true' || this.hasAttribute('data-loaded')) {
      this.#syncLoadedUi();
      return;
    }

    if (typeof IntersectionObserver !== 'function') {
      void this.#loadFromSectionApi();
      return;
    }

    const rootMargin = this.dataset.rootMargin || '200px 0px';

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#observer?.disconnect();
          void this.#loadFromSectionApi();
          break;
        }
      },
      { root: null, rootMargin, threshold: 0 }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  #syncLoadedUi() {
    this.setAttribute('aria-busy', 'false');
    this.dispatchEvent(new CustomEvent('lazy-content-section:loaded', { bubbles: true }));
  }

  /**
   * Fetches this section’s HTML and mounts `.lazy-content-section__inner` contents into this element.
   *
   * @fires lazy-content-section:loaded
   * @fires lazy-content-section:error
   */
  async #loadFromSectionApi() {
    if (this.#loadStarted) return;
    this.#loadStarted = true;

    const rawId = this.dataset.sectionId ?? '';
    const sectionId = normalizeSectionId(rawId);

    if (!sectionId) {
      this.#fail(new Error('lazy-content-section: missing data-section-id'));
      return;
    }

    try {
      const doc = await this.#fetchSectionDocument(sectionId);
      const wrapper = doc.getElementById(buildSectionSelector(sectionId));
      const freshLazy = this.#findFreshLazyElement(wrapper);
      const inner = freshLazy?.querySelector(':scope > .lazy-content-section__inner');

      if (!inner) {
        this.#fail(new Error('lazy-content-section: no .lazy-content-section__inner in section response'));
        return;
      }

      const clonedInner = /** @type {HTMLElement} */ (inner.cloneNode(true));
      this.replaceChildren(clonedInner);
      await this.#activateScripts(clonedInner);

      this.setAttribute('data-loaded', '');
      this.setAttribute('aria-busy', 'false');
      this.dispatchEvent(new CustomEvent('lazy-content-section:loaded', { bubbles: true }));
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.#fail(err);
    }
  }

  /**
   * Finds this element's full-render counterpart in the Section Rendering API response.
   * @param {Element | null} wrapper
   * @returns {Element | null}
   */
  #findFreshLazyElement(wrapper) {
    if (!(wrapper instanceof Element)) return null;

    const blockId = this.dataset.blockId;
    if (blockId) {
      const candidates = wrapper.querySelectorAll('lazy-content-section[data-block-id]');
      for (const candidate of candidates) {
        if (candidate instanceof HTMLElement && candidate.dataset.blockId === blockId) return candidate;
      }
      return null;
    }

    return wrapper.querySelector('lazy-content-section:not([data-block-id])') || wrapper.querySelector('lazy-content-section');
  }

  /**
   * @param {string} sectionId
   * @returns {Promise<string>}
   */
  #fetchSectionHtml(sectionId) {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.sort();
    const key = url.toString();

    let pending = sectionHtmlPromises.get(key);
    if (pending) return pending;

    pending = fetch(key, {
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
    }).then((response) => {
      if (!response.ok) throw new Error(`Section HTTP ${response.status}`);
      return response.text();
    });

    sectionHtmlPromises.set(key, pending);
    pending.catch(() => sectionHtmlPromises.delete(key));
    return pending;
  }

  /**
   * IW performance: parse the shared section response once for nested containers.
   * @param {string} sectionId
   * @returns {Promise<Document>}
   */
  #fetchSectionDocument(sectionId) {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.sort();
    const key = url.toString();

    let pending = sectionDocumentPromises.get(key);
    if (pending) return pending;

    pending = this.#fetchSectionHtml(sectionId).then(async (html) => {
      let doc = new DOMParser().parseFromString(html, 'text/html');
      if (!documentHasEmptySlider(doc) || sectionRetried.has(key)) return doc;

      // First response omitted the product cards. Bust it once.
      sectionRetried.add(key);
      sectionHtmlPromises.delete(key);
      const retryUrl = new URL(url);
      retryUrl.searchParams.set('_cxretry', '1');
      const response = await fetch(retryUrl.toString(), {
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) return doc;
      doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return doc;
    });

    sectionDocumentPromises.set(key, pending);
    pending.catch(() => sectionDocumentPromises.delete(key));
    return pending;
  }

  /**
   * DOMParser keeps scripts inert. Recreate them after mounting so theme/app block
   * behavior remains equivalent to server-rendered content.
   * @param {Element} root
   */
  async #activateScripts(root) {
    const scripts = Array.from(root.querySelectorAll('script'));

    for (const oldScript of scripts) {
      const newScript = document.createElement('script');
      for (const attribute of oldScript.attributes) {
        newScript.setAttribute(attribute.name, attribute.value);
      }

      const type = (oldScript.getAttribute('type') || '').trim().toLowerCase();
      const inertType =
        type &&
        type !== 'text/javascript' &&
        type !== 'application/javascript' &&
        type !== 'module';

      if (inertType) {
        newScript.textContent = oldScript.textContent;
        oldScript.replaceWith(newScript);
        continue;
      }

      const src = oldScript.getAttribute('src');
      if (!src) {
        newScript.textContent = oldScript.textContent;
        oldScript.replaceWith(newScript);
        continue;
      }

      // The Live TV extension currently emits this shared 81 KB bundle once per
      // card group. Its first execution installs the page-level listeners and
      // exposes initCoaCardForms; replaying it adds duplicate global listeners.
      // Keep the guard deliberately narrow because other app scripts can carry
      // per-block configuration through their individual script elements.
      const isLiveTvCardForms = /\/live-tv-card-forms\.js(?:[?#]|$)/i.test(src);
      const themeLtvCardFormsSrc =
        typeof window.__IW_LTV_CARD_FORMS_THEME_SRC__ === 'string'
          ? window.__IW_LTV_CARD_FORMS_THEME_SRC__
          : '';
      if (isLiveTvCardForms && themeLtvCardFormsSrc && src !== themeLtvCardFormsSrc) {
        newScript.src = themeLtvCardFormsSrc;
      }
      if (
        isLiveTvCardForms &&
        (liveTvCardFormsRequested || typeof window.initCoaCardForms === 'function')
      ) {
        oldScript.remove();
        continue;
      }
      if (isLiveTvCardForms) {
        liveTvCardFormsRequested = true;
        var themeLtvUrl =
          typeof window !== 'undefined' && window.__IW_LTV_CARD_FORMS_THEME_URL__;
        if (themeLtvUrl) {
          newScript.src = themeLtvUrl;
        }
      }

      if (!oldScript.hasAttribute('async')) newScript.async = false;

      // The early performance gate can deliberately hold app-extension scripts
      // until first interaction. They have no active src yet, so do not await a
      // load event that cannot fire; the gate will restore them in document order.
      if (newScript._gsrc) {
        if (isLiveTvCardForms) {
          newScript.addEventListener('error', () => { liveTvCardFormsRequested = false; }, { once: true });
        }
        oldScript.replaceWith(newScript);
        continue;
      }

      await new Promise((resolve) => {
        newScript.addEventListener('load', resolve, { once: true });
        newScript.addEventListener(
          'error',
          () => {
            if (isLiveTvCardForms) liveTvCardFormsRequested = false;
            resolve();
          },
          { once: true }
        );
        oldScript.replaceWith(newScript);
      });
    }
  }

  /**
   * @param {Error} error
   */
  #fail(error) {
    this.setAttribute('aria-busy', 'false');
    this.dataset.error = error.message;
    this.dispatchEvent(
      new CustomEvent('lazy-content-section:error', { bubbles: true, detail: { error } })
    );
  }
}

if (!customElements.get('lazy-content-section')) {
  customElements.define('lazy-content-section', LazyContentSection);
}

const repairedSliderIds = new Set();
let repairRunning = false;
let repairQueued = false;

/**
 * The full page can also be cut off inside a product slider. Pull the complete
 * slider from the Section Rendering API and swap the empty shell.
 */
async function repairEmptyProductSliders() {
  if (window.Shopify?.designMode) return;
  if (repairRunning) {
    repairQueued = true;
    return;
  }
  repairRunning = true;

  try {
    /** @type {Map<string, HTMLElement[]>} */
    const groups = new Map();
    document.querySelectorAll('[data-cx-card-slider="true"]').forEach((slider) => {
      if (!(slider instanceof HTMLElement)) return;
      if (!slider.id || repairedSliderIds.has(slider.id)) return;
      const track = slider.querySelector('.custom-container__cards');
      if (!track || track.querySelector('.custom-container__card')) return;
      const section = slider.closest('[id^="shopify-section-"]');
      if (!(section instanceof HTMLElement)) return;
      const sectionId = section.id.replace(/^shopify-section-/, '');
      const list = groups.get(sectionId) || [];
      list.push(slider);
      groups.set(sectionId, list);
    });

    for (const [sectionId, sliders] of groups) {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('_cxretry', '1');
      url.hash = '';
      let html = '';
      try {
        const response = await fetch(url.toString(), {
          headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) continue;
        html = await response.text();
      } catch {
        continue;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      for (const slider of sliders) {
        if (!slider.isConnected || repairedSliderIds.has(slider.id)) continue;
        const fresh = doc.getElementById(slider.id);
        if (!(fresh instanceof HTMLElement)) continue;
        const freshTrack = fresh.querySelector('.custom-container__cards');
        if (!freshTrack || !freshTrack.querySelector('.custom-container__card')) continue;
        repairedSliderIds.add(slider.id);
        const imported = document.importNode(fresh, true);
        slider.replaceWith(imported);
        const init = /** @type {any} */ (window).CX?.initCardSlider;
        if (typeof init === 'function') init(imported);
      }
    }
  } finally {
    repairRunning = false;
  }
  if (repairQueued) {
    repairQueued = false;
    await repairEmptyProductSliders();
  }
}

function scheduleEmptySliderRepair() {
  void repairEmptyProductSliders();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleEmptySliderRepair, { once: true });
} else {
  scheduleEmptySliderRepair();
}

document.addEventListener('lazy-content-section:loaded', scheduleEmptySliderRepair);
